import { useEffect, useState } from "react"
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material"

import { clientApi, projectApi, taskApi } from "../../api"
import { usePermissions } from "../../permissions"
import ClientSelect from "../../components/ClientSelect"
import ProjectSelect from "../../components/ProjectSelect"

/**
 * The same task, somewhere else.
 *
 * ── What this is for ────────────────────────────────────────────────────────
 *
 * "Do for this dealer what we did for that one." The task and its steps are a
 * checklist somebody worked out once; retyping it into the next client's
 * project is slow, and the step that gets left off is always the one that
 * mattered.
 *
 * ── Why it says what will not come with it ──────────────────────────────────
 *
 * Comments, attachments and logged time stay with the original — they belong to
 * the work that was actually done, and carrying them into another client's
 * tenant would be a disclosure however it was asked for. Somebody expecting a
 * full clone and getting a clean one should read that here rather than discover
 * it afterwards.
 */
export default function CopyTaskDialog({ task, subtaskCount = 0, onClose, onCopied }) {
  const { can, isBackOffice } = usePermissions()

  const [clients, setClients] = useState([])
  const [projects, setProjects] = useState([])
  const [clientId, setClientId] = useState("")
  const [projectId, setProjectId] = useState("")
  const [title, setTitle] = useState("")
  const [includeSubtasks, setIncludeSubtasks] = useState(true)
  const [keepStatuses, setKeepStatuses] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!task) return
    setClientId(String(task.client_id))
    setProjectId(task.project_id ? String(task.project_id) : "")
    setTitle(`${task.title} (copy)`)
    setIncludeSubtasks(true)
    setKeepStatuses(false)
    setError(null)

    if (isBackOffice && can("clients", "view")) {
      clientApi
        .list()
        .then((payload) => setClients(payload.organizations || []))
        .catch(() => setClients([]))
    }
  }, [task, isBackOffice, can])

  /* The projects of whichever client is selected, not of the one the task is
     in. Offering the source's projects while a different client is chosen is
     how somebody files work into the wrong tenant and only finds out later. */
  useEffect(() => {
    if (!task || !clientId) return
    projectApi
      .list({ client_id: clientId, limit: 200 })
      .then((payload) => setProjects(payload.projects || []))
      .catch(() => setProjects([]))
  }, [task, clientId])

  const submit = async (event) => {
    event.preventDefault()
    setBusy(true)
    setError(null)
    try {
      const result = await taskApi.copy(task.id, {
        client_id: clientId,
        project_id: projectId ? Number(projectId) : null,
        title: title.trim() || undefined,
        include_subtasks: includeSubtasks,
        keep_statuses: keepStatuses,
      })
      const steps = result.subtasks ? ` with ${result.subtasks} step${result.subtasks === 1 ? "" : "s"}` : ""
      onCopied(`Copied as ${result.task.task_code}${steps}.`)
    } catch (err) {
      setError(err.userMessage || "That task could not be copied.")
    } finally {
      setBusy(false)
    }
  }

  const crossingClients = task && String(clientId) !== String(task.client_id)

  return (
    <Dialog open={Boolean(task)} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <form onSubmit={submit}>
        <DialogTitle>
          Copy “{task?.task_code}”
          <Typography variant="caption" color="text.secondary" display="block">
            A fresh piece of work with the same shape. The original is untouched.
          </Typography>
        </DialogTitle>

        <DialogContent dividers>
          {error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          ) : null}

          <Stack spacing={2}>
            <TextField
              label="Title of the copy"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              fullWidth
              autoFocus
            />

            {isBackOffice && clients.length > 0 ? (
              <ClientSelect
                clients={clients}
                value={clientId}
                onChange={(value) => {
                  setClientId(value)
                  setProjectId("")
                }}
                label="Into which client"
                fullWidth
              />
            ) : null}

            <ProjectSelect
              projects={projects}
              value={projectId}
              onChange={setProjectId}
              label="Into which project"
              emptyLabel="No project — a loose task"
              fullWidth
              helperText="The copy takes its code from this project."
            />

            <Stack>
              <FormControlLabel
                control={
                  <Switch
                    checked={includeSubtasks}
                    onChange={(event) => setIncludeSubtasks(event.target.checked)}
                    disabled={subtaskCount === 0}
                  />
                }
                label={
                  subtaskCount === 0
                    ? "This task has no steps to copy"
                    : `Bring its ${subtaskCount} step${subtaskCount === 1 ? "" : "s"} along`
                }
              />
              <FormControlLabel
                control={<Switch checked={keepStatuses} onChange={(event) => setKeepStatuses(event.target.checked)} />}
                label="Keep where each one had got to"
              />
              <Typography variant="caption" color="text.secondary" sx={{ ml: 5.5, mt: -0.5 }}>
                Off, the copy starts at the beginning — which is what a template usually wants.
              </Typography>
            </Stack>

            <Alert severity="info">
              Comments, attachments and logged time stay with the original.
              {crossingClients ? " Assignees do too — the people on this task are not in that organization." : ""}
            </Alert>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} color="inherit" disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={busy || !clientId}>
            {busy ? "Copying…" : "Copy it"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
